import React from 'react';
import { Link } from 'react-router-dom';
import { useEffectOnce } from 'react-use';
import { useTranslation } from 'react-i18next';

import { CA } from 'contexts';
import { iframeLink } from 'core/iframe';
import { ListVertical } from 'components/list';
import { LinksWithText, Subtitle } from 'components/page';
import { useApi } from 'hooks';
import logo100os from 'assets/images/logo-100os.svg';
import logo100ot from 'assets/images/logo-100ot.svg';

import { CONFIG } from 'config';
import * as Styled from './StyledUserStartups';

const UserStartupsCard = ({ data }) => {
    const { t } = useTranslation('glossary');
    const level = { level: data.level, context: data.level === 0 ? 'pending' : '' };
    return (
        <Styled.UserStartupsCard as={Link} to={data.href}>
            <Styled.CardImage
                img={
                    encodeURI(data.logoUrl) ||
                    (data.typeOfProject === 'tech' ? logo100ot : logo100os)
                }
            />
            <Styled.CardInfos>
                <span className="name">{data.name}</span>
                <span className="code">{data.inductId}</span>
                <span className="bold">{t('level', level)}</span>
            </Styled.CardInfos>
        </Styled.UserStartupsCard>
    );
};

const UserStartupsListItem = ({ data }) => {
    const href = iframeLink(`${CONFIG.appLinks.startup}/home/?startupId=${data._id}`);
    return (
        <Styled.UserStartupsListItem className={data.typeOfProject} as={Link} to={href}>
            <Styled.ListItemImage img={encodeURI(data.logoUrl)} />
            <span className="name">{data.name}</span>
            <span className="code">{data.inductId}</span>
        </Styled.UserStartupsListItem>
    );
};
const UserStartupsList = ({ data }) => (
    <Styled.UserStartupsList>
        <ListVertical items={data} key="_id" itemComponent={UserStartupsListItem} />
    </Styled.UserStartupsList>
);

const Lwtxt = props =>
    !props.text && !props.link ? null : (
        <LinksWithText to={props.href}>
            {props.text && <span className="text">{props.text}</span>}
            {props.link && <span className="link">{props.link}</span>}
        </LinksWithText>
    );

const Title = props => (!props.text ? null : <Subtitle {...props} />);

export function UserStartups(props) {
    const { state } = React.useContext(CA);
    const { t } = useTranslation('components');
    const api = useApi();
    // Get the startup data source
    const [data, setData] = React.useState([]);

    // Get the startup from the api
    let mounted = false;
    useEffectOnce(() => {
        mounted = true;
        const { userid } = state.user;
        if (!data.length || data.length === 0) {
            api.get(`users/${userid}/startups`).then(res => mounted && setData(res));
        }
        return () => {
            mounted = false;
        };
    });

    // Continue only after data is set
    if (!data || !data.length || data.length === 0) {
        return false;
    }

    const hrefStartup = CONFIG.appLinks.startup;

    // Decide witch information to show
    let Component = UserStartupsList;

    // Create a userStartups props object
    let propsData = {
        title: {
            icon: 'far fa-pencil',
            link: t('glossary:edit'),
            text: t('UserStartups.profile'),
        },
        multiple: {
            title: {
                text: t('UserStartups.list'),
                link: false,
            },
        },
        data,
    };

    // Check the list if it has a single one
    if (data.length === 1) {
        // eslint-disable-next-line prefer-destructuring
        propsData.data = propsData.data[0];
        propsData.data.href = iframeLink(`${hrefStartup}/home/?startupId=${propsData.data._id}`);
        propsData.title.href = propsData.data.href;
        Component = UserStartupsCard;
    } else {
        propsData = { ...propsData.multiple, data };
        propsData.title.href = iframeLink(`${hrefStartup}`);
    }

    return !data.length ? null : (
        <Styled.UserStartups multiple={data.length}>
            <Title {...propsData.title} />
            <Component {...propsData} />
            <Lwtxt {...propsData.lwtxt} />
        </Styled.UserStartups>
    );
}
export default UserStartups;
